import { Box, Text } from 'ink';
import { useEffect, useState } from 'react';
import LoadingText from '../../../components/LoadingText';
import ErrorDisplay from '../../../components/ErrorDisplay';
import CustomSelectInputIndicator from '../../../components/CustomSelectInputIndicator';
import SelectInput, { type ItemProps } from '../../../components/SelectInput';
import CliError from '../../../utilities/CliError';
import useExit from '../../../utilities/useExit';
import useInput from '../../../utilities/useInput';
import { getAvailablePlatforms, PlatformInfo } from '../utilities/buildQueries';
import {
  formatIntrospectionStatus,
  getIntrospectionStatusIndicator,
} from '../../../utilities/introspectionStatus';

type State =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'show-platforms'; platforms: PlatformInfo[] };

interface SelectItem {
  label: string;
  value: 'mac' | 'windows' | 'linux';
}

function PlatformItem({ label, isSelected }: ItemProps) {
  return (
    <Box>
      <Text bold={Boolean(isSelected)} color={isSelected ? undefined : 'gray'}>
        {label}
      </Text>
    </Box>
  );
}

export default function PlatformSelector({
  buildId,
  configPath,
  onSelect,
}: {
  buildId: string;
  configPath?: string;
  onSelect: (platform: 'mac' | 'windows' | 'linux') => void;
}) {
  const exit = useExit();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    loadPlatforms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useInput((_, key) => {
    if (key.escape && ['show-platforms', 'loading'].includes(state.status)) {
      exit();
    }
  });

  async function loadPlatforms() {
    try {
      const platforms = await getAvailablePlatforms(buildId, configPath);
      setState({ status: 'show-platforms', platforms });
    } catch (e) {
      setState({ status: 'error', error: CliError.from(e) });
      setTimeout(() => exit(), 10);
    }
  }

  function handleSelect(item: SelectItem) {
    onSelect(item.value);
  }

  switch (state.status) {
    case 'loading':
      return <LoadingText text="Loading available platforms" />;

    case 'error':
      return (
        <ErrorDisplay commandUsed="todesktop introspect" error={state.error} />
      );

    case 'show-platforms': {
      const items: SelectItem[] = state.platforms.map((platform) => {
        const statusLabel = formatIntrospectionStatus(
          platform.breakpointStatus,
          platform.shellStatus,
        );
        return {
          label: `${getIntrospectionStatusIndicator(
            platform.breakpointStatus,
            platform.shellStatus,
          )} ${platform.displayName} (${statusLabel})`,
          value: platform.platform,
        };
      });

      return (
        <Box flexDirection="column">
          <Box marginBottom={1}>
            <Text>Select a platform to introspect:</Text>
          </Box>
          <SelectInput
            items={items}
            onSelect={handleSelect}
            indicatorComponent={CustomSelectInputIndicator}
            itemComponent={PlatformItem}
          />
          <Box marginTop={1}>
            <Text dimColor>
              {state.platforms.length} platform
              {state.platforms.length === 1 ? '' : 's'} available
            </Text>
          </Box>
        </Box>
      );
    }
  }
}
